// PlatesListMobile.tsx
import React, { useMemo, useState } from 'react';
import { 
    Box, Button, Checkbox, Stack, Typography, Card, CardContent, 
    Divider, IconButton, useMediaQuery, useTheme, 
    FormControlLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { FileDownload, Edit, Delete, Assessment, MoreHoriz, CheckBox } from '@mui/icons-material';
import { Plate, EntityPath, ImportRecord } from '../../../types/plate';
import { Group } from '../../../types/group';
import { useFetch } from "../../../hooks/useFetch2";
import { api } from "../../../api/plateApi";
import { api as api2 } from "../../../api/plateApi2";
import { api as groupApi } from "../../../api/groupApi";
import { api as reactionApi } from "../../../api/reactionApi";
import { Subtenant, subtenantApi } from '../../../api/subtenantApi';
import { usePresetDialogs } from '../../dialog';
import { useTranslation } from 'react-i18next';
import { FormModal } from '../../modal/FormModal';
import PlateForm from '../home/PlateForm3';
import PlateReportForm from './PlateReportForm';
import OpenFileComponent from './OpenFileComponent2';
import LocalizedGrid, { CustomGridColDef } from '../../localizedGrid/LocalizedGridCook3h';
import { dataToExcel } from '../../../tools/dataToExcel';
import * as XLSX from 'xlsx';
import i18n from '../../../i18n';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import ActionButtons from '../../ActionButtons2';
import { ImportXlsForm } from './ImportXlsForm';
import { ReactionType } from '../../../types/reaction';
import { formatDateTime } from '../../../tools/util';
import { ToolbarToggleButton } from '../../ToolbarToggleButton';
import { useAuthStore } from '../../../store/useAuthStore3';

interface RowData {
  [key: string]: any; // Ключ — это заголовок столбца
}

export const PlatesList: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const { data, setData, error, update } = useFetch<Plate[]>(api2.fetchPlates, []);
    const { data: entityPaths } = useFetch<EntityPath[]>(api.getEntityPaths, []);
    const { data: groups } = useFetch<Group[]>(groupApi.get, []);
    const { data: reactionTypes } = useFetch<ReactionType[]>(reactionApi.getReactionTypes, []);
    const reactionLookup = Object.fromEntries(
        reactionTypes.map(r => [r.id, r.name])
    );
    const { data: subtenants } = useFetch<Subtenant[]>(subtenantApi.get, []);
    // console.log('reactionTypes', reactionTypes);
    // console.log('reactionLookup', reactionLookup);
    // console.log('plates', data);
    
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [importData, setImportData] = useState<ImportRecord[]>([]);
    const { customerId, subtenantId } = useAuthStore();
    const { showConfirm, DialogComponent } = usePresetDialogs();
    const [selectedPlate, setSelectedPlate] = useState<Plate | null>(null);    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isExtendedOpen, setIsExtendedOpen] = useState(false);
    const [showToolbar, setShowToolbar] = useState<boolean>(false);

// onlyOwn section
    const [onlyOwn, setOnlyOwn] = useState(!subtenantId);

    const handleOwnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setOnlyOwn(event.target.checked);
    };

    const filteredData = useMemo(() => {
        return !onlyOwn ? data : data.filter(row => !row.subtenant_id);
    }, [data, onlyOwn]);
//


    if (!customerId) return <Typography>{t('customer.loading')}</Typography>;

    const columns: CustomGridColDef [] = [
        { field: "id", headerName: "ID", type: "number", flex: 1, width: 50, },
        { field: "registration_number", headerName: t('PlatesList.registration_number'), type: "string", flex: 2, width: 100, },
        { field: "group_id", headerName: t('PlatesList.group'), type: "singleSelect", flex: 2, width: 100, display: 'flex',
            valueOptions: groups.map( group => ({value: group.group_id, label: group.name})),
            editable: false,
            renderCell: (params: GridRenderCellParams<Group>) => {
                return (
                    <Typography>
                        {groups.find(g => g.group_id === params.row.group_id)?.name}
                    </Typography>
                );
            }
        },
        { field: "subtenant_id", headerName: t('PlatesList.subtenant'), type: "singleSelect", flex: 2, width: 100, display: 'flex',
            valueOptions: subtenants.map( subtenant => ({value: subtenant.subtenant_id, label: subtenant.name})),
            editable: false,
            hidden: onlyOwn || !!subtenantId,
            renderCell: (params: GridRenderCellParams<Subtenant>) => {
                return (
                    <Typography>
                        {subtenants.find(s => s.subtenant_id === params.row.subtenant_id)?.name}
                    </Typography>
                );
            }
        },
        { field: "car_owner", headerName: t('PlatesList.car_owner'), type: "string", flex: 2, width: 100, },
        { field: "car_brand", headerName: t('PlatesList.car_brand'), type: "string", flex: 2, width: 100, },
        { field: "tag", headerName: t('PlatesList.tag'), type: "string", flex: 2, width: 100, },
        { field: "valid_since", headerName: t('PlatesList.valid_since'), type: "string", flex: 2, width: 100, valueFormatter: (v) => formatDateTime(v)},
        { field: "valid_to", headerName: t('PlatesList.valid_to'), type: "string", flex: 2, width: 100, valueFormatter: (v) => formatDateTime(v)},
        {
            field: 'is_otp',
            headerName: t('PlatesList.is_otp'),
            width: 120,
            renderCell: (params: GridRenderCellParams<Plate>) => (
                <Checkbox checked={params.value} disabled />
            ),
        },
        {
            field: 'is_enabled',
            headerName: t('PlatesList.is_enabled'),
            width: 120,
            renderCell: (params: GridRenderCellParams<Plate>) => (
                <Checkbox checked={params.value} disabled />
            ),
        },
        {
            field: 'reactions',
            headerName: t('PlatesList.reactions'),
            width: 120,
            type: "string",
            valueGetter: (value: number[]) => {
                if (!value || !Array.isArray(value)) return '';
                
                // console.log('valueGetter value', value, 'reactionLookup', reactionLookup);
                
                return value
                .map(id => reactionLookup[id] || 'Unknown') // берем label по ID
                .join(', '); // склеиваем в строку
            },
        },
        {
            field: 'actions',
            headerName: t('common.actions'),
            width: 150,
            sortable: false,
            renderCell: (params: GridRenderCellParams<Plate>) => {
                return (
                    <ActionButtons actions={[
                        {type: 'edit', action: () => handleOpenModal(params.row), },
                        {type: 'delete', action: () => handleDelete2(params.row.id || -1), },
                        {type: 'extended', action: () => handleExtended(params.row), tooltip: ('Звіт проїздів')},
                    ]} />
                );
            },
        },        
    ];

    // --- Handlers (Оставлены без изменений для совместимости) ---
    const handleOpenModal = (plate: Plate | null) => {
        setSelectedPlate(plate);
        setIsModalOpen(true);
    };

    const handleExtended = (plate: Plate | null) => {
        setSelectedPlate(plate);
        setIsExtendedOpen(true);
    };

    const handleDelete2 = async (id: number) => {
        const result = await showConfirm(t('common.delete_question', {id}), t('common.delete_confirmation'));
        if (!result) return;
        await api2.deletePlate(id);
        setData((prev) => prev.filter(p => p.id !== id));
    };

    const handleDelete = async (id: number) => {
        const result = await showConfirm(t('common.delete_question', {id}), t('common.delete_confirmation'));
        if (!result) return;
        await api.delete(id);
        setData((prev) => prev.filter(p => p.id !== id));
    };

    const handleUpdate2 = async (edited: Plate) => {
        if (selectedPlate) {
            const res = await api2.upsertPlate(edited);
            setData(prev => prev.map(p => p.id === edited.id ? edited : p));
        } else {
            const res = await api2.upsertPlate(edited);
            const newData = {...edited, id: res.id};
            setData(prev => [...prev, newData]);
        }
        setIsModalOpen(false);
    }

    const handleUpdate = async (edited: Plate) => {
        if (selectedPlate) {
            await api.put(edited);
            setData(prev => prev.map(p => p.id === edited.id ? edited : p));
        } else {
            const newData = await api.postAndSynchronize(edited);
            setData(prev => [...prev, newData]);
        }
        setIsModalOpen(false);
    };

    const handleExport = async () => {
        setIsExporting(true);
        const filename = 'lpr_export';
        try {
            // const data0 = await api.getExportData();
            const data0 = await api.get();
            const data1 = data0.map((item: any) => ({
                registration_number: item.registration_number,
                car_owner: item.car_owner,
                car_brand: item.car_brand,
                tag: item.tag,
                is_enabled: item.is_enabled,
                is_otp: item.is_otp,
                valid_since: formatDateTime(item.valid_since,'L'),
                valid_to: formatDateTime(item.valid_to,'L'),
            }));
            dataToExcel(data1, filename);
            console.log("Export successful");
        } catch (error) {
            console.error("Export failed:", error);
        } finally {
            setIsExporting(false);
        }
    };

    const handleOpenImport = (file: File) => {
        console.log('file is opened', file.name);
        console.log('file', file);
        const reader = new FileReader();
        reader.onload = async (e) => {
            const data = e.target?.result;
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const json: RowData[] = XLSX.utils.sheet_to_json(worksheet);
            const importRecords: ImportRecord[] = json.map((row, index) => {
                return {
                    id: index,
                    registration_number: String(row.registration_number).trim(),
                    car_owner: String(row.car_owner || '').trim(),
                    car_brand: String(row.car_brand || '').trim(),
                    tag: String(row.tag || '').trim(),
                    is_enabled: Boolean(row.is_enabled || false),
                    is_otp: Boolean(row.is_otp || false),
                    valid_since: String(row.valid_since || '').trim(),
                    valid_to: String(row.valid_to || '').trim(),
                } as ImportRecord;
            });            
            // setImportData(importRecords);
            setImportData([]);
            try {
                // выполняем нормализацию для корректного сравнения
                const rawNumbers = importRecords.map(r => r.registration_number );
                const normalizedResponse = await api.normalizeNumbers(rawNumbers);
                const normalizationMap = new Map(
                    normalizedResponse.map(item => [item.plate, item.plate_normalized])
                );
                const normalizedRecords = importRecords.map(record => ({
                    ...record,
                    registration_number: normalizationMap.get(record.registration_number) || record.registration_number
                }));
                setImportData(normalizedRecords); 
            } catch (error) {
                console.error("Ошибка нормализации:", error);
                // todo: Показать ошибку пользователю
            }
        }
        reader.readAsArrayBuffer(file);
        setIsImportOpen(true);
    };

    const handleCloseExtended = () => setIsExtendedOpen(false);
    const handleCloseImport = (refresh: boolean) => {
        setIsImportOpen(false);
        
        if (refresh) update();

    }

    // --- Рендер карточки для мобильных устройств ---
    const renderMobileCard = (plate: Plate) => (
        <Card key={plate.id} sx={{ mb: 2, boxShadow: 2 }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" color="primary">
                        {plate.registration_number}
                    </Typography>
                    <Box>
                        <IconButton onClick={() => handleOpenModal(plate)} size="small"><Edit /></IconButton>
                        <IconButton onClick={() => handleDelete2(plate.id || -1)} size="small" color="error"><Delete /></IconButton>
                        <IconButton onClick={() => { setSelectedPlate(plate); setIsExtendedOpen(true); }} size="small"><MoreHoriz /></IconButton>
                    </Box>
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                <Stack spacing={0.5}>
                    <Typography variant="body2"><strong>{t('PlatesList.group')}:</strong> {groups.find(g => g.group_id === plate.group_id)?.name}</Typography>
                    <Typography variant="body2"><strong>{t('PlatesList.car_owner')}:</strong> {plate.car_owner}</Typography>
                    <Typography variant="body2"><strong>{t('PlatesList.car_brand')}:</strong> {plate.car_brand}</Typography>
                    <Box display="flex" alignItems="center">
                        <Typography variant="body2"><strong>OTP:</strong></Typography>
                        <Checkbox checked={plate.is_otp} disabled size="small" />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {plate.valid_since} — {plate.valid_to}
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    );

    return (
        <Box sx={{ width: '100%', p: isMobile ? 1 : 0 }}>
            {DialogComponent}

            {/* Панель кнопок: на мобилках делаем вертикальный стек или скролл */}
            <Stack 
                direction={isMobile ? "column" : "row"} 
                spacing={2} 
                sx={{ mb: 2 }}
            >
                <Button 
                    fullWidth={isMobile}
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenModal(null)}
                >
                    {t('PlatesList.New_Regnumber')}
                </Button>

                <Stack direction="row" spacing={1} flexGrow={1}>
                    <Button 
                        fullWidth={isMobile}
                        variant="outlined" 
                        startIcon={<FileDownload />}
                        onClick={() => handleExport()}
                    >
                        {t('PlatesList.Export_Regnumber')}
                    </Button>
                    <OpenFileComponent
                        title={t('PlatesList.Import_Regnumber')}
                        accept='.xls,.xlsx,.csv'
                        onFileSelect={(file: File) => handleOpenImport(file)}
                    />
                    <ToolbarToggleButton 
                        isOpen={showToolbar} 
                        onToggleState={setShowToolbar} 
                    />

                    {!isMobile && !subtenantId && (
                    <FormControlLabel
                        control={
                            <Checkbox 
                                checked={onlyOwn} 
                                onChange={handleOwnChange} 
                                color="primary"
                            />
                        }
                        label={t('PlatesList.Only_Own')}
                        sx={{ 
                            whiteSpace: 'nowrap', // Чтобы текст не переносился на новую строку
                            marginLeft: isMobile ? 0 : 1 // Небольшой отступ слева на десктопе
                        }}
                    />
                    )}
            </Stack>
    
            </Stack>

            {/* Основной контент: Сетка или Карточки */}
            {isMobile ? (
                <Box sx={{ pb: 2 }}>
                    {data.map(plate => renderMobileCard(plate))}
                </Box>
            ) : (
                <LocalizedGrid
                    lang={i18n.language as any}
                    gridId='PlatesListMobile'
                    rows={filteredData}
                    columns={columns}
                    getRowId={(row) => row.id}
                    initialState={{
                        columns: {columnVisibilityModel: {id: false}}
                    }}
                    hideToolbar={!showToolbar}
                />
            )}

            <Typography color='error'>{error?.message}</Typography>

            {/* Модальные окна (остаются без изменений) */}
            <FormModal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <PlateForm
                    title={!!selectedPlate ? t('PlatesList.LicensePlate') : t('PlatesList.NewLicensePlate')}
                    // subtitle={!!selectedPlate ? 'Перепустка' : 'Перепустка'}
                    subtitle={t('Access_pass')}
                    plate={selectedPlate}
                    groups={groups}
                    subtenants={subtenants}
                    entityPaths={entityPaths}
                    onSave={handleUpdate2}
                    onCancel={() => setIsModalOpen(false)}
                    reactionTypes={reactionTypes}
                />
            </FormModal>
            
            <FormModal open={isExtendedOpen} onClose={handleCloseExtended}>
                <PlateReportForm
                    // title={'Звіт проїздів'}
                    title={t('PlateList.PassageReport')}
                    plate={selectedPlate}
                    onCancel={handleCloseExtended}
                />
            </FormModal>

            <FormModal open={isImportOpen} onClose={() => handleCloseImport(false)}>
                <ImportXlsForm
                    data={importData}
                    exitsdata={data}
                    onClose={handleCloseImport}
                />
                {/* <Typography>Modal content goes here.</Typography> */}
            </FormModal>
        </Box>
    );
};